// Context that contains the metadata of the current story.
import React, { Component, createContext, useMemo, useState } from 'react';
import { prefetchWordMetadata } from '../services/WordMetadataService';

const CurrentStoryWordMetadataContext = createContext({});

const CurrentStoryWordMetadataProvider = ({ children }) => {
  const [allWordMetadata, setAllWordMetadata] = useState(null); // initialize as two-dimensional array
  const [currentWordMetadata, setCurrentWordMetadata] = useState(null);
  const [currentPhraseWordMetadata, setCurrentPhraseWordMetadata] = useState(null);
  // Keep this cached for some places like changeOnePart.  Makes things a lot easier for those places
  const [previousWordMetadata, setPreviousWordMetadata] = useState(null);

  // Keep track of this to stop needless state updates
  const [wordIndex, setWordIndex] = useState(undefined);
  const [phraseIndex, setPhraseIndex] = useState(undefined);

  // For the one place in reader manager we just call it for a single phrase
  const initializePhraseWordMetadata = async (story, phraseIndex, currentWordIndex) => {
    const newMetadata = { ...allWordMetadata };
    if (story && story.phrases && story.phrases[phraseIndex]) {
      await prefetchWordMetadata(phrase,phraseIndex,story.metadataOverrides).then(list => {
        if (list) {
          list.forEach((metadata, wordIndex) => {
            newMetadata[phraseIndex][wordIndex] = metadata;
          })
        }
      });
      setAllWordMetadata( newMetadata );
    }
  }

  // Function to update the metadata based on overrides
  const initializeStoryWordMetadata = (story, overallPhraseIndex, currentWordIndex) => {
    const newMetadata = {};
    if (story && story.phrases) {
      Promise.allSettled(story.phrases.map((phrase,phraseIndex)=>{
        newMetadata[phraseIndex] = {};
        return prefetchWordMetadata(phrase,phraseIndex,story.metadataOverrides).then(list => {
          if (list) {
            list.forEach((metadata, wordIndex) => {
              newMetadata[phraseIndex][wordIndex] = metadata;
            })
          }
        });
      })).then(() => {
        setAllWordMetadata( newMetadata );
        updateWordMetadata(overallPhraseIndex, currentWordIndex);
      });
    }
  }

  // Effect to update selected word metadata based on index changes
  const updateWordMetadata = (overallPhraseIndex, currentWordIndex) => {
    if (allWordMetadata && (phraseIndex !== overallPhraseIndex || wordIndex !== currentWordIndex)) {
      setPreviousWordMetadata(currentWordMetadata);
      // Ensure indices are within bounds
      if (allWordMetadata[overallPhraseIndex]) {
        setCurrentWordMetadata(allWordMetadata[overallPhraseIndex][currentWordIndex]);
      } else {
        setCurrentWordMetadata(null);
      }
      setCurrentPhraseWordMetadata(allWordMetadata[overallPhraseIndex]);

      setWordIndex(currentWordIndex);
      setPhraseIndex(overallPhraseIndex);
    }
  }

  const contextValue = {
    initializePhraseWordMetadata,
    initializeStoryWordMetadata,
    updateWordMetadata,
    wordMetadata: allWordMetadata,
    currentWordMetadata,
    currentPhraseWordMetadata,
    previousWordMetadata,
  };

  return (
    <CurrentStoryWordMetadataContext.Provider value={contextValue}>
      {children}
    </CurrentStoryWordMetadataContext.Provider>
  );
};

// Create a HOC to provide context as props to class components
const withCurrentStoryWordMetadata = (WrappedComponent) => {
  return class extends Component {
    render() {
      return (
        <CurrentStoryWordMetadataContext.Consumer>
          {context => <WrappedComponent {...{...this.props, ...context}} />}
        </CurrentStoryWordMetadataContext.Consumer>
      );
    }
  };
};

export { CurrentStoryWordMetadataProvider, withCurrentStoryWordMetadata };